import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const passwordChecks = [
  {
    key: "length",
    label: "At least 8 characters",
    test: (value) => String(value || "").length >= 8,
  },
  {
    key: "uppercase",
    label: "One uppercase letter",
    test: (value) => /[A-Z]/.test(String(value || "")),
  },
  {
    key: "number",
    label: "One number",
    test: (value) => /\d/.test(String(value || "")),
  },
  {
    key: "special",
    label: "One special character",
    test: (value) => /[^A-Za-z0-9]/.test(String(value || "")),
  },
];

const ChangePasswordModal = ({ show, onHide }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [visiblePassword, setVisiblePassword] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const strength = useMemo(() => {
    const passed = passwordChecks.filter((rule) => rule.test(newPassword)).length;

    if (!newPassword) {
      return { score: 0, label: "Not started", className: "empty" };
    }

    if (passed <= 1) {
      return { score: passed, label: "Weak", className: "weak" };
    }

    if (passed <= 3) {
      return { score: passed, label: "Good", className: "good" };
    }

    return { score: passed, label: "Strong", className: "strong" };
  }, [newPassword]);

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setVisiblePassword({
      current: false,
      next: false,
      confirm: false,
    });
    setErrors({});
    setIsSaving(false);
  };

  useEffect(() => {
    if (!show) {
      resetForm();
      return undefined;
    }

    document.body.classList.add("ns-change-password-open");

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSaving) {
        onHide?.();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.classList.remove("ns-change-password-open");
      window.removeEventListener("keydown", handleEscape);
    };
  }, [show, isSaving, onHide]);

  const clearError = (field) => {
    if (!errors[field]) return;
    setErrors((previous) => ({ ...previous, [field]: "" }));
  };

  const validateForm = () => {
    const formErrors = {};

    if (!currentPassword.trim()) {
      formErrors.currentPassword = "Current password is required";
    }

    if (!newPassword.trim()) {
      formErrors.newPassword = "New password is required";
    }

    if (!confirmPassword.trim()) {
      formErrors.confirmPassword = "Confirm password is required";
    } else if (newPassword !== confirmPassword) {
      formErrors.confirmPassword = "Passwords do not match";
    }

    if (currentPassword && newPassword && currentPassword === newPassword) {
      formErrors.newPassword = "New password must be different from current password";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleClose = () => {
    if (isSaving) return;
    onHide?.();
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (!validateForm()) return;

    try {
      setIsSaving(true);

      const response = await axios.post(
        `${apiUrl}/users/change-password`,
        {
          oldPassword: currentPassword,
          newPassword,
          confirmPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data?.message || "Password changed successfully");
      onHide?.();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to change password");
      console.error("change-password error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleVisibility = (key) => {
    setVisiblePassword((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const Field = ({
    label,
    required,
    error,
    value,
    onChange,
    placeholder,
    visibleKey,
    autoComplete,
  }) => (
    <div className="ns-cp-field">
      <label>
        {label}
        {required ? <span>*</span> : null}
      </label>

      <div className={`ns-cp-input-wrap ${error ? "has-error" : ""}`}>
        <input
          type={visiblePassword[visibleKey] ? "text" : "password"}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
        />

        <button
          type="button"
          className="ns-cp-eye-btn"
          onClick={() => toggleVisibility(visibleKey)}
          tabIndex={-1}
        >
          {visiblePassword[visibleKey] ? "Hide" : "Show"}
        </button>
      </div>

      {error ? <div className="ns-cp-error">{error}</div> : null}
    </div>
  );

  if (!show) return null;

  return (
    <div className="ns-cp-root" role="dialog" aria-modal="true" aria-label="Change password">
      <style>{`
        body.ns-change-password-open {
          overflow: hidden !important;
        }

        .ns-cp-root {
          position: fixed;
          inset: 0;
          z-index: 2147483000;
          display: flex;
          justify-content: flex-end;
          background: rgba(15, 23, 42, 0.08);
          backdrop-filter: blur(1.5px);
          -webkit-backdrop-filter: blur(1.5px);
        }

        .ns-cp-backdrop {
          position: absolute;
          inset: 0;
          border: 0;
          background: transparent;
          padding: 0;
          cursor: pointer;
        }

        .ns-cp-drawer {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: min(520px, 100vw);
          height: 100vh;
          height: 100dvh;
          z-index: 2147483001;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 34%),
            #f8fafc;
          box-shadow: -18px 0 52px rgba(15, 23, 42, 0.16);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .ns-cp-header {
          flex: 0 0 auto;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.14), transparent 36%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          padding: 18px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 14px;
        }

        .ns-cp-title {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          min-width: 0;
        }

        .ns-cp-icon {
          width: 48px;
          height: 48px;
          border-radius: 18px;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.22);
          font-size: 1.2rem;
          font-weight: 950;
          flex: 0 0 auto;
        }

        .ns-cp-eyebrow {
          color: #2563eb;
          font-size: 0.68rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .ns-cp-header h4 {
          color: #001f5b;
          font-size: 1.24rem;
          line-height: 1.14;
          font-weight: 950;
          margin: 0;
        }

        .ns-cp-header p {
          color: #64748b;
          font-size: 0.78rem;
          line-height: 1.42;
          font-weight: 760;
          margin: 5px 0 0;
        }

        .ns-cp-close {
          width: 40px;
          height: 40px;
          border-radius: 14px;
          border: 1px solid rgba(220, 38, 38, 0.16);
          background: #fff1f2;
          color: #dc2626;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 1.28rem;
          line-height: 1;
          flex: 0 0 auto;
        }

        .ns-cp-body {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          padding: 15px 18px 112px;
          scrollbar-width: thin;
          scrollbar-color: #93c5fd #e2e8f0;
        }

        .ns-cp-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.055);
          padding: 15px;
          margin-bottom: 13px;
        }

        .ns-cp-card-title {
          color: #0f172a;
          display: block;
          font-size: 0.9rem;
          font-weight: 950;
          margin-bottom: 4px;
        }

        .ns-cp-card-copy {
          color: #64748b;
          display: block;
          font-size: 0.74rem;
          line-height: 1.42;
          font-weight: 760;
          margin-bottom: 14px;
        }

        .ns-cp-field {
          display: grid;
          gap: 7px;
          margin-bottom: 13px;
        }

        .ns-cp-field:last-child {
          margin-bottom: 0;
        }

        .ns-cp-field label {
          color: #0f172a;
          font-size: 0.77rem;
          font-weight: 950;
          margin: 0;
        }

        .ns-cp-field label span {
          color: #dc2626;
          margin-left: 3px;
        }

        .ns-cp-input-wrap {
          width: 100%;
          min-height: 44px;
          border-radius: 14px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: #ffffff;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          overflow: hidden;
          transition: border-color 0.16s ease, box-shadow 0.16s ease;
        }

        .ns-cp-input-wrap:focus-within {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .ns-cp-input-wrap.has-error {
          border-color: #dc2626;
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.10);
        }

        .ns-cp-input-wrap input {
          width: 100%;
          min-height: 42px;
          border: 0;
          outline: none;
          background: transparent;
          color: #0f172a;
          font-size: 0.88rem;
          font-weight: 760;
          padding: 0 13px;
        }

        .ns-cp-input-wrap input::placeholder {
          color: #94a3b8;
        }

        .ns-cp-eye-btn {
          height: 30px;
          min-width: 56px;
          border: 0;
          border-left: 1px solid rgba(15, 23, 42, 0.08);
          background: #f8fafc;
          color: #2563eb;
          font-size: 0.72rem;
          font-weight: 950;
          padding: 0 10px;
          cursor: pointer;
        }

        .ns-cp-error {
          color: #dc2626;
          font-size: 0.72rem;
          font-weight: 820;
          line-height: 1.35;
        }

        .ns-cp-strength {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.045);
          padding: 15px;
        }

        .ns-cp-strength-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
        }

        .ns-cp-strength-head span {
          color: #0f172a;
          font-size: 0.82rem;
          font-weight: 950;
        }

        .ns-cp-strength-badge {
          min-height: 26px;
          border-radius: 999px;
          padding: 4px 9px;
          display: inline-flex;
          align-items: center;
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
          font-size: 0.68rem;
          font-weight: 950;
        }

        .ns-cp-strength-badge.weak {
          background: #fff1f2;
          color: #be123c;
          border-color: #fecdd3;
        }

        .ns-cp-strength-badge.good {
          background: #fffbeb;
          color: #b45309;
          border-color: #fde68a;
        }

        .ns-cp-strength-badge.strong {
          background: #ecfdf5;
          color: #047857;
          border-color: #bbf7d0;
        }

        .ns-cp-meter {
          height: 8px;
          border-radius: 999px;
          background: #e2e8f0;
          overflow: hidden;
          margin-bottom: 12px;
        }

        .ns-cp-meter span {
          display: block;
          height: 100%;
          width: var(--strength-width, 0%);
          border-radius: 999px;
          background: #94a3b8;
          transition: width 0.18s ease, background 0.18s ease;
        }

        .ns-cp-meter span.weak {
          background: #f43f5e;
        }

        .ns-cp-meter span.good {
          background: #f59e0b;
        }

        .ns-cp-meter span.strong {
          background: #22c55e;
        }

        .ns-cp-checks {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }

        .ns-cp-check {
          min-height: 30px;
          border-radius: 999px;
          background: #f8fafc;
          color: #64748b;
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 5px 9px;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 0.7rem;
          font-weight: 850;
        }

        .ns-cp-check.is-valid {
          background: #ecfdf5;
          color: #047857;
          border-color: #bbf7d0;
        }

        .ns-cp-check-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: currentColor;
          flex: 0 0 auto;
        }

        .ns-cp-footer {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.96);
          border-top: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 -14px 34px rgba(15, 23, 42, 0.08);
          padding: 13px 18px;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          z-index: 4;
        }

        .ns-cp-btn {
          min-height: 42px;
          border-radius: 14px;
          padding: 0 18px;
          border: 1px solid rgba(15, 23, 42, 0.10);
          font-size: 0.82rem;
          font-weight: 950;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          white-space: nowrap;
          transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
        }

        .ns-cp-btn:hover:not(:disabled) {
          transform: translateY(-1px);
        }

        .ns-cp-btn:disabled {
          opacity: 0.62;
          cursor: not-allowed;
        }

        .ns-cp-btn.secondary {
          background: #ffffff;
          color: #334155;
        }

        .ns-cp-btn.primary {
          min-width: 168px;
          background: linear-gradient(135deg, #16a34a, #22c55e);
          border-color: #16a34a;
          color: #ffffff;
          box-shadow: 0 14px 30px rgba(22, 163, 74, 0.20);
        }

        @media (max-width: 620px) {
          .ns-cp-drawer {
            width: 100vw;
          }

          .ns-cp-checks {
            grid-template-columns: 1fr;
          }

          .ns-cp-footer {
            display: grid;
            grid-template-columns: 1fr;
          }

          .ns-cp-btn {
            width: 100%;
          }
        }
      `}</style>

      <button
        type="button"
        className="ns-cp-backdrop"
        aria-label="Close change password"
        onClick={handleClose}
      />

      <form className="ns-cp-drawer" onSubmit={handleSubmit}>
        <header className="ns-cp-header">
          <div className="ns-cp-title">
            <span className="ns-cp-icon">🔐</span>
            <div>
              <div className="ns-cp-eyebrow">Security</div>
              <h4>Change Password</h4>
              <p>Update your password to keep your account secure.</p>
            </div>
          </div>

          <button type="button" className="ns-cp-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </header>

        <main className="ns-cp-body">
          <section className="ns-cp-card">
            <span className="ns-cp-card-title">Password Details</span>
            <span className="ns-cp-card-copy">
              Enter your current password and choose a new password. Use a unique password that is not used elsewhere.
            </span>

            <Field
              label="Current Password"
              required
              error={errors.currentPassword}
              value={currentPassword}
              onChange={(value) => {
                setCurrentPassword(value);
                clearError("currentPassword");
              }}
              placeholder="Enter current password"
              visibleKey="current"
              autoComplete="current-password"
            />

            <Field
              label="New Password"
              required
              error={errors.newPassword}
              value={newPassword}
              onChange={(value) => {
                setNewPassword(value);
                clearError("newPassword");
              }}
              placeholder="Enter new password"
              visibleKey="next"
              autoComplete="new-password"
            />

            <Field
              label="Confirm Password"
              required
              error={errors.confirmPassword}
              value={confirmPassword}
              onChange={(value) => {
                setConfirmPassword(value);
                clearError("confirmPassword");
              }}
              placeholder="Re-enter new password"
              visibleKey="confirm"
              autoComplete="new-password"
            />
          </section>

          <section className="ns-cp-strength">
            <div className="ns-cp-strength-head">
              <span>Password strength</span>
              <span className={`ns-cp-strength-badge ${strength.className}`}>
                {strength.label}
              </span>
            </div>

            <div className="ns-cp-meter" style={{ "--strength-width": `${(strength.score / 4) * 100}%` }}>
              <span className={strength.className} />
            </div>

            <div className="ns-cp-checks">
              {passwordChecks.map((rule) => {
                const valid = rule.test(newPassword);

                return (
                  <span className={`ns-cp-check ${valid ? "is-valid" : ""}`} key={rule.key}>
                    <span className="ns-cp-check-dot" />
                    {rule.label}
                  </span>
                );
              })}
            </div>
          </section>
        </main>

        <footer className="ns-cp-footer">
          <button className="ns-cp-btn secondary" type="button" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>

          <button className="ns-cp-btn primary" type="submit" disabled={isSaving}>
            {isSaving ? "Changing..." : "Change Password"}
          </button>
        </footer>
      </form>
    </div>
  );
};

export default ChangePasswordModal;
